import BaseService from "./BaseService";

export type SkuShipbobProduct = {
  shipbobProductId: number;
  shipbobVariantId: number;
  sku: string | null;
  name: string | null;
  channelId: string | null;
  channelName: string | null;
  referenceId: string | null;
  isBundle: boolean;
  lastSyncedAt?: string;
};

export type SkuShopifyMapping = {
  id: number;
  store: "US" | "AU" | "CA" | "UK";
  shopifyProductId: string | null;
  shopifyVariantId: string;
  shopifySku: string | null;
  title: string | null;
  isPrimary: boolean;
  matchedBy: string | null;
};

export type UnmatchedVariant = {
  store: "US" | "AU" | "CA" | "UK";
  productId: string;
  variantId: string;
  sku: string | null;
  title: string;
  ambiguousSku: boolean;
};

export type FreightMethod = "40hc" | "40gp" | "20dc" | "lcl" | "fob";

/** One ShipBob inventory id carried by more than one NetSuite item (from the last NetSuite sync) */
export type NetSuiteItemConflict = {
  inventoryId: string;
  items: Array<{ netsuiteItemId: string; itemid: string; displayName: string | null }>;
};

export type CurrentPrice = {
  unitPrice: number;
  currency: string;
  quoteDate: string | null;
  validUntil: string | null;
  isExpired: boolean;
};

export type ExtractedQuoteLine = {
  description: string;
  size: string | null;
  dimensions: string | null;
  manufacturerSku: string | null;
  pcsPerCarton: number | null;
  prices: Array<{ method: FreightMethod; unitPrice: number }>;
  loadingQuantities: Array<{ method: FreightMethod; qty: number }>;
  suggestions: Array<{ itemId: number; label: string; score: number; matchedBy: string }>;
  decisive: boolean;
};

export type ExtractedQuote = {
  fileName: string;
  sourcePdfPath: string;
  manufacturer: string | null;
  quotationNumber: string | null;
  quoteDate: string | null;
  validUntil: string | null;
  currency: string;
  moq: string | null;
  paymentTerms: string | null;
  deliveryTerms: string | null;
  lines: ExtractedQuoteLine[];
};

export type QuoteConfirmRow = {
  itemId: number;
  method: FreightMethod;
  unitPrice: number;
  currency?: string;
  manufacturer?: string | null;
  quotationNumber?: string | null;
  lineDescription?: string | null;
  quoteDate?: string | null;
  validUntil?: string | null;
  moq?: string | null;
  paymentTerms?: string | null;
  deliveryTerms?: string | null;
  pcsPerCarton?: number | null;
  loadingQty?: number | null;
  sourcePdfPath?: string | null;
};

export type PackComponent = { inventoryId: string; qty: number; source: "danican_number" | "manual" };

export type SkuItem = {
  shipbobInventoryId: number;
  shipbobSku: string | null;
  name: string | null;
  manufacturerSku: string | null;
  barcode: string | null;
  manufacturer: string | null;
  unitLength: number | null;
  unitWidth: number | null;
  unitHeight: number | null;
  unitDimUnit: string | null;
  unitWeight: number | null;
  unitWeightUnit: string | null;
  pcsPerCarton: number | null;
  cartonLength: number | null;
  cartonWidth: number | null;
  cartonHeight: number | null;
  cartonDimUnit: string | null;
  cartonWeight: number | null;
  cartonWeightUnit: string | null;
  netsuiteItemId: string | null;
  /** NetSuite's own item SKU (itemid), e.g. "SBL-101-DZ" — differs from the manufacturer SKU */
  netsuiteSku: string | null;
  /** Per-product tariff rate override (fraction); blank = the supplier's default applies */
  tariffRate: number | null;
  status: string | null;
  notes: string | null;
  fulfillableQty: number | null;
  onhandQty: number | null;
  pickedQty: number;
  prices: Partial<Record<FreightMethod, CurrentPrice>>;
  shopifyMappings: SkuShopifyMapping[];
  lastSyncedAt: string | null;
  shipbobProducts: SkuShipbobProduct[];
  usedInBundles: number;
  /** What this item contains when it is a multi-pack (2-pack = 2 x the single item) */
  packComponents: PackComponent[];
  /** The multi-packs this single item is sold in */
  soldInPacks: Array<{ inventoryId: string; qty: number }>;
  shopifyMappingCount: number;
  quoteCounts: { total: number; confirmed: number };
};

export type SkuItemsResponse = {
  items: SkuItem[];
  bundles: SkuShipbobProduct[];
  pickedRange: { start: string; end: string };
};

class SkuMasterService extends BaseService {
  async getItems(params?: { pickedStart?: string; pickedEnd?: string }): Promise<SkuItemsResponse | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("sku-master/items", { params }),
      "Failed to fetch /sku-master/items",
    );
  }

  /**
   * Upload quote PDFs for extraction + match suggestions. Throws with the
   * server's message on failure. Nothing is saved until confirmQuotes.
   */
  async extractQuotes(files: File[]): Promise<{ quotes: ExtractedQuote[] }> {
    await this._getAccessToken();

    const form = new FormData();
    for (const file of files) form.append("files", file);
    try {
      const response = await this.http.post("sku-master/quotes/extract", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 300000,
      });
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Quote extraction failed");
    }
  }

  async confirmQuotes(rows: QuoteConfirmRow[]): Promise<{ ok: boolean; saved: number } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("sku-master/quotes/confirm", { rows }),
      "Failed to save quotes",
    );
  }

  async syncShopify(): Promise<{ ok: boolean; summary: Record<string, unknown> } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("sku-master/sync/shopify"),
      "Failed to run Shopify sync",
    );
  }

  async getNetSuiteConflicts(): Promise<{ syncedAt: string | null; conflicts: NetSuiteItemConflict[] } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("sku-master/netsuite-conflicts"),
      "Failed to fetch NetSuite item conflicts",
    );
  }

  /** Stamps each item's NetSuite item id from NetSuite (read-only there). */
  async syncNetSuite(): Promise<{ ok: boolean; summary: Record<string, unknown> } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("sku-master/sync/netsuite"),
      "Failed to run NetSuite item sync",
    );
  }

  async getShopifyReview(): Promise<{ unmatched: UnmatchedVariant[]; totalVariants: number } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("sku-master/shopify-review"),
      "Failed to fetch mapping review queue",
    );
  }

  async assignShopifyMapping(mapping: {
    store: string;
    shopifyVariantId: string;
    shopifyProductId: string | null;
    shopifySku: string | null;
    title: string | null;
    itemId: number;
  }): Promise<{ ok: boolean } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("sku-master/shopify-mappings", mapping),
      "Failed to assign mapping",
    );
  }

  async ignoreShopifyVariant(target: { store: string; shopifyVariantId: string; reason?: string }): Promise<{ ok: boolean } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("sku-master/shopify-mapping-ignores", target),
      "Failed to ignore variant",
    );
  }

  /**
   * Throws (rather than returning null) so the grid can show the server's
   * actual message — e.g. the manufacturer-SKU uniqueness conflict naming the
   * item that already holds the part number.
   */
  async updateItem(
    inventoryId: number,
    updates: Partial<Pick<SkuItem, "manufacturerSku" | "pcsPerCarton" | "cartonLength" | "cartonWidth" | "cartonHeight" | "cartonWeight" | "manufacturer" | "notes" | "tariffRate">>,
  ): Promise<{ ok: boolean }> {
    await this._getAccessToken();

    try {
      const response = await this.http.patch(`sku-master/items/${inventoryId}`, updates);
      return response.data;
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to update SKU master item");
    }
  }

  /** Set what a multi-pack contains, by hand. An empty list makes it a plain single item again. */
  async setPackComponents(inventoryId: number, components: Array<{ inventoryId: string; qty: number }>): Promise<void> {
    await this._getAccessToken();
    try {
      await this.http.put(`sku-master/items/${inventoryId}/pack-components`, { components });
    } catch (err: any) {
      throw new Error(err?.response?.data?.error || "Failed to save the pack's contents");
    }
  }

  async syncShipBob(): Promise<{ ok: boolean; summary: Record<string, unknown> } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.post("sku-master/sync/shipbob"),
      "Failed to run ShipBob sync",
    );
  }

  async getSyncLog(): Promise<{ runs: Array<Record<string, unknown>> } | null> {
    await this._getAccessToken();

    return this.handleHttpRequest(
      () => this.http.get("sku-master/sync-log"),
      "Failed to fetch sync log",
    );
  }
}

export default SkuMasterService;
